import { REGEX } from 'constants/common';
import { PaymentMode, UpdateMerchantCustomizationsPayload } from './settings.model';
import { PaymentCategory, PaymentCategoryTypes, PaymentSubCategoryTypes, SubCategory } from './type';
import { store } from 'store/store';
import { CommissionBasesSourceType } from './constants';
import { EstimateSettingsReminder } from './estimate-settings/estimateSetting.model';
import { InvoiceSettingsReminder } from './invoice-settings/invoiceSettings.model';
import { ReminderConfig, ScheduleRelation } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from './constants';

function organizePaymentModesByCategory(modes: PaymentMode[]): PaymentCategory[] {
  // Initialize a map to group modes by categories
  const categoryMap: { [key: string]: PaymentCategory } = {
    [PaymentCategoryTypes.SELF_CHECKOUT]: {
      displayName: 'Customer self-checkout',
      type: PaymentCategoryTypes.SELF_CHECKOUT,
      paymentModes: [],
      subCategories: []
    },
    [PaymentCategoryTypes.ASSISTED_CHECKOUT]: {
      displayName: 'Assisted checkout',
      type: PaymentCategoryTypes.ASSISTED_CHECKOUT,
      paymentModes: [],
      subCategories: []
    },
    [PaymentCategoryTypes.MANUAL]: {
      displayName: 'Record payments manually',
      type: PaymentCategoryTypes.MANUAL,
      paymentModes: [],
      subCategories: []
    }
  };

  // Process each mode and assign it to the correct category/subCategory
  modes.forEach(mode => {
    const { category, subCategory } = mode;

    // Get the appropriate category object from the map
    const categoryObj = categoryMap[category.type];
    if (categoryObj) {
      if (subCategory) {
        // Handle subCategory logic: find or create the subCategory in the category
        let subCatObj = categoryObj.subCategories?.find(subCat => subCat.type === subCategory.type);

        if (!subCatObj) {
          subCatObj = {
            displayName: subCategory.displayName,
            type: subCategory.type as PaymentSubCategoryTypes,
            paymentModes: []
          };
          categoryObj.subCategories!.push(subCatObj);
        }

        // Add the mode to the subCategory's paymentModes array
        subCatObj.paymentModes.push(mode);
      } else {
        // If no subCategory, add the mode directly to the category's paymentModes array
        categoryObj.paymentModes.push(mode);
      }
    }
  });

  // Now merge any duplicate subCategories
  Object.values(categoryMap).forEach(category => {
    if (category.subCategories) {
      const mergedSubCategories: { [key: string]: SubCategory } = {};

      category.subCategories.forEach(subCategory => {
        // If this subcategory type already exists in the merged map, combine payment modes
        if (mergedSubCategories[subCategory.type]) {
          mergedSubCategories[subCategory.type].paymentModes.push(...subCategory.paymentModes);
        } else {
          // If it's the first time this subcategory type is encountered, add it
          mergedSubCategories[subCategory.type] = subCategory;
        }
      });

      // Replace the subCategories array with the merged version
      category.subCategories = Object.values(mergedSubCategories);
    }
  });

  // Return the categories in the order of PaymentCategoryTypes
  return [
    categoryMap[PaymentCategoryTypes.SELF_CHECKOUT],
    categoryMap[PaymentCategoryTypes.ASSISTED_CHECKOUT],
    categoryMap[PaymentCategoryTypes.MANUAL]
  ];
}

function formatCustomizationPayload(data: UpdateMerchantCustomizationsPayload) {
  const { paymentModes } = data;
  if (paymentModes) {
    const formattedCustomizationData = {
      ...data,
      paymentModes: paymentModes.map((mode: PaymentMode) => ({ id: mode.id, enabled: mode.enabled }))
    };
    return formattedCustomizationData;
  }
  return data;
}

const isValidDecimalInput = (val: string): boolean => {
  return val === '' || REGEX.maximumTwoDecimals.test(val);
};

const cleanNumberInput = (val: string): string => {
  return val.replace(REGEX.nonNumberRegExp, '');
};

const getCommissionBases = ({ type }: { type: CommissionBasesSourceType }) => {
  const { rootReducer } = store.getState();
  const { commissionBases } = rootReducer.app;
  if (type === CommissionBasesSourceType.REFERRAL) {
    return commissionBases.commissionBaseList.filter(item => item.isEnabledForReferrals);
  }
  return commissionBases.commissionBaseList.filter(item => item.isEnabledForCommissions);
};

const getCommissionBaseLabel = (key: string) => {
  if (!key) {
    return '';
  }
  if (key === 'NA') {
    return '-';
  }
  const { rootReducer } = store.getState();
  const { commissionBases } = rootReducer.app;
  return commissionBases.commissionBaseMap[key?.toUpperCase()]?.label || key;
};

type SettingsReminder = EstimateSettingsReminder | InvoiceSettingsReminder;

// Transform API response reminders to component format
const transformSettingApiToComponentReminders = (
  apiReminders: SettingsReminder[],
  includeReminderId = true
): ReminderConfig[] => {
  if (!apiReminders || apiReminders.length === 0) {
    return [];
  }

  return apiReminders.map((reminder, index) => ({
    id: `${index + 1}-${reminder.entityType}`,
    reminderNumber: index + 1,
    scheduleRelation: (reminder.condition.timing as ScheduleRelation) || ScheduleRelation.BEFORE,
    days: reminder.condition.offset,
    reminderDate: null,
    entityType: reminder.entityType,
    reminderId: includeReminderId ? reminder.id : null
  }));
};

// Transform component reminders to API payload format
const transformComponentToSettingApiReminders = (
  componentReminders: ReminderConfig[],
  applyOn: string,
  entityType: string
): EstimateSettingsReminder[] => {
  if (!componentReminders || componentReminders.length === 0) {
    return [];
  }

  return componentReminders.map((reminder, index) => ({
    id: reminder.reminderId,
    name: `Reminder ${index + 1}`,
    entityType: reminder.entityType ?? entityType,
    condition: {
      applyOn: applyOn,
      timing: reminder.scheduleRelation,
      offset: reminder.days
    }
  }));
};

// Checks if there are duplicate reminders based on schedule relation and days
const hasDuplicateReminders = (reminders: ReminderConfig[]): boolean => {
  const seen = {};
  return reminders.some(reminder => {
    const key = `${reminder.scheduleRelation}-${reminder.days}`;
    if (seen[key]) return true;
    seen[key] = true;
    return false;
  });
};

// Formats a reminder configuration into a human-readable text
const formatReminderText = (
  reminder: ReminderConfig,
  dateType = ReminderDateTypeLabel.EXPIRY_DATE,
  includeDateType = true
): string => {
  if (reminder.scheduleRelation === ScheduleRelation.ON) {
    return `On ${dateType}`;
  }

  const daysText = reminder.days === 1 ? 'day' : 'days';
  const relationText = reminder.scheduleRelation === ScheduleRelation.BEFORE ? 'before' : 'after';

  return includeDateType
    ? `${reminder.days} ${daysText} ${relationText} ${dateType}`
    : `${reminder.days} ${daysText} ${relationText}`;
};

export {
  organizePaymentModesByCategory,
  formatCustomizationPayload,
  isValidDecimalInput,
  cleanNumberInput,
  getCommissionBases,
  getCommissionBaseLabel,
  transformSettingApiToComponentReminders,
  transformComponentToSettingApiReminders,
  hasDuplicateReminders,
  formatReminderText
};
